import { useState } from "react";

import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { MapPin, Tag } from "lucide-react";

import { useItem } from "@/api/items";
import { useLocationName } from "@/api/locations";
import { LocationCombobox } from "@/components/entity-select/LocationCombobox";
import { PageHeader } from "@/components/page-header";
import { LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import { BulkLabelsDialog } from "../BulkLabelsDialog";
import ExecutionsSheet from "./ExecutionsSheet";
import LocationPanel from "./LocationPanel";
import SourcesSheet from "./SourcesSheet";

export default function ItemsDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const itemQuery = useItem(id ? Number(id) : undefined);
  const locationName = useLocationName();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showEditLabels, setShowEditLabels] = useState(false);
  const [execOpen, setExecOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  if (itemQuery.isLoading || !itemQuery.data) {
    return <LoadingState label={t("items.detail.loading")} />;
  }

  const data = itemQuery.data;
  const configs = data.planningConfigs ?? [];
  const configByLocation = new Map(configs.map((c) => [c.location, c]));
  const pinnedLocationIds = configs.map((c) => c.location);

  const locParam = searchParams.get("loc");
  // Only a positive-integer id counts; a junk param (?loc=abc) falls back to the
  // default rather than poisoning the render with NaN (NaN isn't caught by ??).
  const parsedLoc =
    locParam && /^\d+$/.test(locParam) ? Number(locParam) : null;
  const activeLocationId =
    parsedLoc ?? (pinnedLocationIds.length > 0 ? pinnedLocationIds[0] : null);
  const isTransient =
    activeLocationId !== null && !pinnedLocationIds.includes(activeLocationId);

  function selectLocation(locationId: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("loc", String(locationId));
        return next;
      },
      { replace: false },
    );
  }

  const tagsRow = (
    <div className="flex flex-wrap items-center gap-2">
      {data.labels?.map((l) => (
        <Badge key={l.id} variant="outline">
          {l.name}
        </Badge>
      ))}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowEditLabels(true)}
        className="text-muted-foreground"
      >
        <Tag className="mr-1 size-3" />
        {t("items.editTags")}
      </Button>
      {showEditLabels && (
        <BulkLabelsDialog
          items={[data]}
          onClose={() => setShowEditLabels(false)}
        />
      )}
    </div>
  );

  const actions = (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={() => setExecOpen(true)}>
        {t("items.detail.executionsButton")}
      </Button>
      <Button variant="outline" size="sm" onClick={() => setSourcesOpen(true)}>
        {t("items.detail.itemSources")}
      </Button>
    </div>
  );

  const sheets = (
    <>
      <ExecutionsSheet
        itemId={data.id}
        itemName={data.name}
        open={execOpen}
        onOpenChange={setExecOpen}
      />
      <SourcesSheet
        itemId={data.id}
        itemName={data.name}
        open={sourcesOpen}
        onOpenChange={setSourcesOpen}
      />
    </>
  );

  const tabBar = (
    <div className="flex items-center gap-2">
      <TabsList variant="line">
        {configs.map((c) => (
          <TabsTrigger key={c.location} value={String(c.location)}>
            {locationName(c.location)}
          </TabsTrigger>
        ))}
      </TabsList>
      {/*
        The "explore" slot: one combobox that doubles as the transient tab.
        When you're viewing a non-configured location it shows that location's
        name with a dashed underline (temporary); otherwise it's a "Location"
        placeholder. Selecting re-points this single slot — there's no separate
        "+", so it never reads as "add another tab".
      */}
      <LocationCombobox
        value={isTransient ? activeLocationId : null}
        onChange={(locId) => {
          if (locId !== null) selectLocation(locId);
        }}
        variant="ghost"
        size="sm"
        ariaLabel={t("items.detail.locationPicker.trigger")}
        triggerClassName={cn(
          // Mirror a line-variant tab: same text states (fixes the ghost-button
          // accent-on-hover glitch) and, when active, the same solid brand
          // underline as a selected tab — only italic distinguishes it.
          "relative h-9 w-auto gap-1 rounded-none border-0 bg-transparent px-2 text-sm font-medium shadow-none transition-all",
          "text-foreground/60 hover:bg-transparent hover:text-foreground",
          isTransient &&
            "text-foreground italic after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary after:opacity-100",
        )}
        renderTriggerContent={(loc) =>
          loc ? (
            <span>{loc.name}</span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <MapPin className="size-3" />
              {t("items.detail.locationPicker.trigger")}
            </span>
          )
        }
      />
    </div>
  );

  return (
    <Tabs
      value={activeLocationId !== null ? String(activeLocationId) : ""}
      onValueChange={(v) => selectLocation(Number(v))}
      className="gap-6"
    >
      <PageHeader
        title={data.name}
        meta={tagsRow}
        actions={actions}
        tabs={tabBar}
      />

      {activeLocationId !== null ? (
        <TabsContent value={String(activeLocationId)}>
          <LocationPanel
            itemId={data.id}
            locationId={activeLocationId}
            config={configByLocation.get(activeLocationId) ?? null}
          />
        </TabsContent>
      ) : (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <MapPin />
            </EmptyMedia>
            <EmptyTitle>{t("items.detail.selectLocation.title")}</EmptyTitle>
            <EmptyDescription>
              {t("items.detail.selectLocation.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      {sheets}
    </Tabs>
  );
}
